/**
 * Cálculo de "rotting" (negócio parado) isolado da feature de pipeline.
 *
 * Um card está "apodrecendo" quando o estágio define `rotting_days` (> 0) e a
 * última atividade da conversa é mais antiga que esse número de dias. Reusa o
 * `getDayDifferenceFromNow` do helper de tempo do repo (que trabalha com
 * timestamp Unix em segundos).
 */
import { getUnixTime } from 'date-fns';
import { getDayDifferenceFromNow } from 'shared/helpers/timeHelper';

/**
 * Dias desde a última atividade. Retorna null quando a data é inválida/ausente.
 * @param {string} lastActivityAt - ISO string.
 * @param {Date} [now=new Date()]
 * @returns {number|null}
 */
export const daysSinceActivity = (lastActivityAt, now = new Date()) => {
  if (!lastActivityAt) return null;
  const unix = getUnixTime(new Date(lastActivityAt));
  if (Number.isNaN(unix)) return null;
  return getDayDifferenceFromNow(now, unix);
};

/**
 * Quantos dias o card está parado ALÉM do limite do estágio.
 * Retorna 0 quando não está apodrecendo (ou quando o estágio não define limite).
 * @param {Object} params
 * @param {number|null} params.rottingDays - limite do estágio.
 * @param {string} params.lastActivityAt - ISO da última atividade.
 * @param {Date} [params.now=new Date()]
 * @returns {number}
 */
export const rottingDays = ({ rottingDays: limit, lastActivityAt, now }) => {
  if (!limit || limit <= 0) return 0;
  const days = daysSinceActivity(lastActivityAt, now);
  if (days === null || days < limit) return 0;
  return days;
};

/**
 * Conveniência booleana: o card está apodrecendo?
 * @param {Object} params - mesmos de `rottingDays`.
 * @returns {boolean}
 */
export const isRotting = params => rottingDays(params) > 0;
